import React, { useEffect, useState } from 'react';
import { theme } from '../../styles/theme';

interface BrandIntroProps {
    onComplete: () => void;
}

const BrandIntro: React.FC<BrandIntroProps> = ({ onComplete }) => {
    const [stage, setStage] = useState(0);

    useEffect(() => {
        // Stage 0: Show puzzle piece falling (1.2s)
        if (stage === 0) {
            const timer = setTimeout(() => setStage(1), 1200);
            return () => clearTimeout(timer);
        }
        // Stage 1: Show second puzzle piece (1.2s)
        if (stage === 1) {
            const timer = setTimeout(() => setStage(2), 1200);
            return () => clearTimeout(timer);
        }
        // Stage 2: Show full logo and text (2s)
        if (stage === 2) {
            const timer = setTimeout(() => {
                onComplete();
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [stage, onComplete]);

    return (
        <div style={{
            minHeight: '100vh',
            background: theme.gradients.nature,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            overflow: 'hidden',
        }}>
            <style>{`
                @keyframes fallAndSpin {
                    0% {
                        opacity: 0;
                        transform: translateY(-100px) rotate(0deg);
                    }
                    50% {
                        opacity: 1;
                    }
                    100% {
                        opacity: 1;
                        transform: translateY(0) rotate(360deg);
                    }
                }

                @keyframes pulse {
                    0%, 100% {
                        transform: scale(1);
                    }
                    50% {
                        transform: scale(1.1);
                    }
                }

                @keyframes fadeInUp {
                    0% {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    100% {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .puzzle-piece-1 {
                    animation: fallAndSpin 1.2s ease-in-out forwards;
                }

                .puzzle-piece-2 {
                    animation: fallAndSpin 1.2s ease-in-out 0.3s forwards;
                    opacity: 0;
                }

                .logo-container {
                    animation: fadeInUp 0.8s ease-out forwards;
                    opacity: 0;
                }

                .tagline {
                    animation: fadeInUp 0.8s ease-out 0.3s forwards;
                    opacity: 0;
                }
            `}</style>

            {/* Falling puzzle pieces */}
            <div style={{
                position: 'relative',
                width: '200px',
                height: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '40px',
            }}>
                {/* First piece */}
                <div
                    className="puzzle-piece-1"
                    style={{
                        position: 'absolute',
                        fontSize: '80px',
                        opacity: stage >= 0 ? 1 : 0,
                    }}
                >
                    🧩
                </div>

                {/* Second piece */}
                <div
                    className="puzzle-piece-2"
                    style={{
                        position: 'absolute',
                        fontSize: '80px',
                        left: '40px',
                        opacity: stage >= 1 ? 1 : 0,
                    }}
                >
                    🧩
                </div>

                {/* Third piece */}
                <div
                    className="puzzle-piece-2"
                    style={{
                        position: 'absolute',
                        fontSize: '80px',
                        right: '40px',
                        opacity: stage >= 1 ? 1 : 0,
                    }}
                >
                    🧩
                </div>
            </div>

            {/* Logo and text appear after pieces */}
            {stage >= 2 && (
                <>
                    <div className="logo-container" style={{
                        textAlign: 'center',
                    }}>
                        <h1 style={{
                            fontSize: '48px',
                            fontWeight: 900,
                            background: theme.gradients.primary,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            margin: '0 0 12px 0',
                            letterSpacing: '-1px',
                        }}>
                            Puzzzled
                        </h1>
                    </div>
                    <div className="tagline" style={{
                        fontSize: '18px',
                        color: theme.colors.textSecondary,
                        textAlign: 'center',
                        maxWidth: '300px',
                    }}>
                        Discover the joy of beautiful jigsaw puzzles
                    </div>
                </>
            )}

            {/* Loading indicator during animations */}
            {stage < 2 && (
                <div style={{
                    marginTop: '60px',
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'center',
                }}>
                    {[0, 1, 2].map((i) => (
                        <div
                            key={i}
                            style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: theme.colors.accent,
                                opacity: stage > i ? 1 : 0.3,
                                transition: 'opacity 0.3s ease',
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default BrandIntro;
